import type { MonopolyPayload } from '../report';
import { fmtInt, fmtPct, niceMax } from '../utils';
import { GroupedBars, KpiTile, Panel } from './Primitives';
import { LAST_YEAR_DERIVED_DAYS, LAST_YEAR_SERIES, LAST_YEAR_TOTALS, lastYearDate, type ComparisonMetric } from '../lastYear';

/*
 * 2026 vs 2025, day by day.
 *
 * THE COMPARISON IS LIKE FOR LIKE. 2026 is mid-campaign, so every figure here
 * covers campaign days 1..N of BOTH years — comparing a partial campaign with
 * last year's finished one would flatter this year on nothing but elapsed time.
 * Last year's full-campaign total is quoted separately, as context, never as
 * the thing 2026 is measured against.
 *
 * Where 2025 has no figure for a day (its consent chart simply has no entry for
 * 09/09 or for 25/09–01/10) the bar is a gap, the tooltip says so, and that day
 * is excluded from BOTH sides of the delta — otherwise 2026 would win a day
 * 2025 never reported.
 */

const METRICS: Array<{
  key: ComparisonMetric;
  title: string;
  kpi: string;
  thisYear: (d: MonopolyPayload) => number[] | undefined;
  what: string;
}> = [
  {
    key: 'participants',
    title: 'Participants uniques',
    kpi: 'Participants uniques',
    thisYear: (d) => d.participants.newByDay,
    what: 'Nouveaux participants inscrits ce jour-là',
  },
  {
    key: 'visits',
    title: 'Visites',
    kpi: 'Visites',
    thisYear: (d) => d.visits.byDay,
    what: 'Visites enregistrées ce jour-là',
  },
  {
    key: 'consents',
    title: 'Consentements',
    kpi: 'Consentements',
    thisYear: (d) => d.participants.consentsByDay,
    what: 'Consentements collectés ce jour-là, tous canaux',
  },
];

/** Sum of a series over days where BOTH years have a figure. */
function pairedTotals(now: number[], then: Array<number | null>) {
  let a = 0;
  let b = 0;
  let skipped = 0;
  for (let i = 0; i < now.length; i += 1) {
    const last = then[i];
    if (last === null || last === undefined) {
      skipped += 1;
      continue;
    }
    a += now[i];
    b += last;
  }
  return { a, b, skipped };
}

function Delta({ now, then }: { now: number; then: number }) {
  if (then <= 0) return null;
  const pct = ((now - then) / then) * 100;
  const up = pct >= 0;
  return (
    <span className={`mono-delta${up ? ' up' : ' down'}`}>
      {up ? '▲' : '▼'} {fmtPct(Math.abs(pct))} vs 2025
    </span>
  );
}

export function CompareTab({ data }: { data: MonopolyPayload }) {
  const { days } = data;
  const dayLabels = days.map((_, i) => `J${i + 1}`);
  const tipTitles = days.map(
    (d, i) => `Jour ${i + 1} · ${d.slice(8, 10)}/${d.slice(5, 7)}/2026 vs ${lastYearDate(i + 1).slice(8, 10)}/${lastYearDate(i + 1).slice(5, 7)}/2025`,
  );

  return (
    <>
      <div className="mono-grid-3">
        {METRICS.map((m) => {
          const now = m.thisYear(data);
          const then = LAST_YEAR_SERIES[m.key].slice(0, days.length);
          if (!now) return <KpiTile key={m.key} label={m.kpi} value="—" sub="pas encore publié par le pipeline" />;
          const { a, b } = pairedTotals(now, then);
          return <KpiTile key={m.key} label={m.kpi} value={fmtInt(a)} sub={<Delta now={a} then={b} />} />;
        })}
      </div>

      {METRICS.map((m) => {
        const now = m.thisYear(data);
        const then = LAST_YEAR_SERIES[m.key].slice(0, days.length);
        if (!now) {
          return (
            <Panel key={m.key} title={`${m.title} — 2026 vs 2025`}>
              <p className="mono-note">
                Cette série n&apos;est pas encore publiée par le pipeline&nbsp;: le graphique apparaîtra au prochain rafraîchissement.
              </p>
            </Panel>
          );
        }
        const { a, b, skipped } = pairedTotals(now, then);
        const values = now.map((v, i) => ({ a: v, b: then[i] ?? null }));
        const max = niceMax([...now, ...then.filter((v): v is number => v !== null)]);
        const derived = LAST_YEAR_DERIVED_DAYS[m.key].filter((d) => d <= days.length).length;
        return (
          <Panel
            key={m.key}
            title={`${m.title} — 2026 vs 2025`}
            action={
              <span className="mono-note">
                {fmtInt(a)} en 2026 · {fmtInt(b)} en 2025 sur les mêmes jours <Delta now={a} then={b} />
              </span>
            }
          >
            <GroupedBars
              values={values}
              days={days}
              max={max}
              height={240}
              labels={{ a: `${m.title} 2026`, b: `${m.title} 2025` }}
              xLabels={dayLabels}
              tipTitles={tipTitles}
              missingLabel="pas de relevé"
              cumulative
              note={m.what}
              ariaLabel={`${m.title} par jour de campagne, 2026 comparé à 2025`}
            />
            <p className="mono-foot">
              Jours 1 à {days.length} des deux campagnes (2025 a démarré le 09/09, 2026 le 08/09).
              {skipped > 0 && ` ${skipped} jour${skipped > 1 ? 's' : ''} sans relevé en 2025, exclus des deux totaux.`}
              {` Campagne 2025 complète sur 35 jours : ${fmtInt(LAST_YEAR_TOTALS[m.key])}.`}
              {derived > 0 && ` ${derived} valeur${derived > 1 ? 's' : ''} 2025 estimée${derived > 1 ? 's' : ''} à partir du graphique d'origine (±1 %).`}
            </p>
          </Panel>
        );
      })}
      <p className="mono-foot">
        Source 2025&nbsp;: export Power BI du client «&nbsp;Realtime Dashboard (Monopoly)&nbsp;», 09/09/2025 – 13/10/2025. Chiffres figés, saisis dans
        l&apos;application&nbsp;: il n&apos;existe ni pipeline ni base 2025 à interroger.
      </p>
    </>
  );
}
